import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { doc, addDoc, collection } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getContrastText } from '../utils/colorUtils';
import emailjs from '@emailjs/browser';

const helpSections = [
  {
    title: 'Calendário',
    items: [
      {
        question: 'Como funciona a visualização do calendário?',
        answer: 'O calendário exibe seus hábitos e eventos programados para cada dia do mês. Você pode marcar hábitos como concluídos, ver eventos, e personalizar a visualização nas configurações.'
      },
      {
        question: 'O que significam as cores e ícones do calendário?',
        answer: 'A legenda abaixo da tabela de hábitos explica o significado das cores: verde para dia perfeito, azul para hábito realizado, amarelo para o dia atual, e caixas vazias para dias programados.'
      },
      {
        question: 'Como personalizar o calendário?',
        answer: 'Clique no ícone de engrenagem para acessar as configurações do calendário, onde é possível escolher quais componentes exibir, ordem, cores e destaques.'
      }
    ]
  },
  {
    title: 'Hábitos',
    items: [
      {
        question: 'Como adicionar um novo hábito?',
        answer: 'Para adicionar um novo hábito, clique no botão "Adicionar" no topo da página. Preencha o nome do hábito e selecione os dias da semana em que você deseja realizá-lo.'
      },
      {
        question: 'Como marcar um hábito como concluído?',
        answer: 'Para marcar um hábito como concluído, clique no botão de check ao lado do hábito. O hábito será marcado como concluído para o dia atual.'
      },
      {
        question: 'Como editar ou excluir um hábito?',
        answer: 'Cada hábito possui botões de edição e exclusão. O botão de edição permite modificar o nome e os dias do hábito, enquanto o botão de exclusão remove o hábito permanentemente.'
      }
    ]
  },
  {
    title: 'Metas',
    items: [
      {
        question: 'Como criar uma nova meta?',
        answer: 'Para criar uma nova meta, clique no botão "Adicionar" no topo da página. Defina um título, descrição, prazo e adicione as subtarefas necessárias para alcançar sua meta.'
      },
      {
        question: 'Como acompanhar o progresso de uma meta?',
        answer: 'Cada meta possui uma barra de progresso que é atualizada conforme você completa as subtarefas. O progresso é calculado automaticamente com base nas subtarefas concluídas.'
      },
      {
        question: 'Como marcar uma meta como concluída?',
        answer: 'Uma meta pode ser marcada como concluída manualmente através do botão de check, ou será automaticamente concluída quando todas as subtarefas forem completadas.'
      }
    ]
  },
  {
    title: 'Desafios',
    items: [
      {
        question: 'Como participar de um desafio?',
        answer: 'Para participar de um desafio, clique no botão "Adicionar" e escolha entre os modelos disponíveis ou crie um desafio personalizado. Defina a data de início e as tarefas do desafio.'
      },
      {
        question: 'Como funciona o sistema de progresso?',
        answer: 'O progresso do desafio é calculado com base nas tarefas concluídas. Cada tarefa marcada como concluída contribui para o progresso geral do desafio.'
      },
      {
        question: 'Posso personalizar um desafio?',
        answer: 'Sim, você pode criar desafios personalizados definindo suas próprias tarefas, duração e categoria. Também é possível editar desafios existentes para ajustá-los às suas necessidades.'
      }
    ]
  },
  {
    title: 'Notas',
    items: [
      {
        question: 'Como criar uma nova nota?',
        answer: 'Para criar uma nova nota, clique no botão "Adicionar". Você pode adicionar um título e o conteúdo da nota. As notas são salvas automaticamente.'
      },
      {
        question: 'Como organizar minhas notas?',
        answer: 'As notas podem ser filtradas por categoria e data. Use os filtros disponíveis no topo da página para encontrar notas específicas.'
      },
      {
        question: 'Como editar ou excluir uma nota?',
        answer: 'Cada nota possui botões de edição e exclusão. O botão de edição permite modificar o conteúdo da nota, enquanto o botão de exclusão remove a nota permanentemente.'
      }
    ]
  },
  {
    title: 'Meditações',
    items: [
      {
        question: 'Como usar as meditações guiadas?',
        answer: 'As meditações estão organizadas por categorias como Ansiedade, Relaxamento, Medos, etc. Clique no botão de play ao lado da meditação desejada para começar. Se a meditação ainda não estiver disponível, você será notificado.'
      },
      {
        question: 'Como controlar a reprodução?',
        answer: 'Use o mini player na parte inferior da tela para controlar a reprodução. Você pode pausar, retomar, ajustar o volume e o progresso da meditação. O player também mostra o título da meditação atual.'
      },
      {
        question: 'Posso usar outras funcionalidades durante a meditação?',
        answer: 'Sim! A meditação continuará tocando enquanto você navega por outras partes do aplicativo. O mini player permanece sempre visível para que você possa controlar a reprodução a qualquer momento.'
      }
    ]
  },
  {
    title: 'Eventos',
    items: [
      {
        question: 'Como adicionar um novo evento?',
        answer: 'Clique no botão de adicionar evento, preencha as informações como título, data e horário, e salve. O evento aparecerá no calendário e na lista de eventos.'
      },
      {
        question: 'Como editar ou excluir um evento?',
        answer: 'Na lista de eventos ou no calendário, clique no ícone de edição ao lado do evento para alterar informações, ou no ícone de lixeira para excluir.'
      },
      {
        question: 'Como visualizar eventos do mês?',
        answer: 'A aba de eventos e o calendário mostram todos os eventos do mês selecionado, ordenados por data e horário.'
      }
    ]
  },
  {
    title: 'Estatísticas',
    items: [
      {
        question: 'O que são as estatísticas?',
        answer: 'A aba de estatísticas mostra gráficos e indicadores sobre seus hábitos, metas e progresso mensal, ajudando a acompanhar sua evolução.'
      },
      {
        question: 'Como interpretar os gráficos?',
        answer: 'Os gráficos mostram informações como dias perfeitos, hábitos realizados, produtividade por dia da semana e comparativos de metas.'
      }
    ]
  },
  {
    title: 'Dicas de Livros',
    items: [
      {
        question: 'Como receber recomendações de livros?',
        answer: 'Na aba de dicas de livros, você pode buscar por temas, autores ou gêneros e receber sugestões personalizadas.'
      },
      {
        question: 'Posso salvar livros favoritos?',
        answer: 'Sim! Clique no ícone de favorito para salvar livros que você gostou e acessar depois.'
      }
    ]
  },
  {
    title: 'Configurações',
    items: [
      {
        question: 'Quais opções posso personalizar?',
        answer: 'Na aba de configurações, você pode ajustar cores, preferências de visualização, notificações, layout do calendário e muito mais.'
      },
      {
        question: 'Como restaurar o layout padrão?',
        answer: 'No menu de configurações do calendário, clique em "Layout padrão" para restaurar a ordem e visibilidade dos componentes.'
      }
    ]
  }
];

function Help({ customColors }) {
  const { currentUser } = useAuth();
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    emailjs.init("YOUR_PUBLIC_KEY");
  }, []);

  const handleOpenFeedback = () => {
    setOpenFeedback(true);
  };

  const handleCloseFeedback = () => {
    setOpenFeedback(false);
    setFeedbackType('');
    setFeedbackMessage('');
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackType || !feedbackMessage) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha todos os campos',
        severity: 'error'
      });
      return;
    }

    try {
      const templateParams = {
        feedback_type: feedbackType,
        message: feedbackMessage,
        user_email: currentUser?.email || 'Anônimo',
        user_id: currentUser?.uid || 'Anônimo',
        timestamp: new Date().toLocaleString('pt-BR')
      };

      await emailjs.send(
        'service_9jqal6b',
        'template_slhuwwi',
        templateParams,
        'lnpyqE8vVSiyN6044'
      );

      setSnackbar({
        open: true,
        message: 'Feedback enviado com sucesso! Obrigado pela sua contribuição.',
        severity: 'success'
      });
      handleCloseFeedback();
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao enviar feedback. Tente novamente mais tarde.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <PageLayout
      title="Ajuda"
      hideAddButton
      customColors={customColors}
      actionButtons={
        <Button
          variant="contained"
          startIcon={<FeedbackIcon />}
          onClick={handleOpenFeedback}
          sx={{
            bgcolor: customColors?.primary || 'primary.main',
            color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
            '&:hover': {
              bgcolor: customColors?.primary || 'primary.main',
              opacity: 0.9
            }
          }}
        >
          Enviar Feedback
        </Button>
      }
    >
      <Grid container spacing={3}>
        {helpSections.map((section) => (
          <Grid item xs={12} md={6} key={section.title}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color={customColors?.primary || 'primary.main'}>
                  {section.title}
                </Typography>
                {section.items.map((item, index) => (
                  <Accordion key={index} sx={{ mt: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '&.Mui-expanded': {
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': {
                            margin: '12px 0'
                          }
                        }
                      }}
                    >
                      <Typography>{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openFeedback} onClose={handleCloseFeedback}>
        <DialogTitle>Enviar Feedback</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Tipo de Feedback</InputLabel>
            <Select
              value={feedbackType}
              label="Tipo de Feedback"
              onChange={(e) => setFeedbackType(e.target.value)}
            >
              <MenuItem value="sugestao">Sugestão</MenuItem>
              <MenuItem value="bug">Reportar Bug</MenuItem>
              <MenuItem value="duvida">Dúvida</MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Sua mensagem"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedback}>Cancelar</Button>
          <Button onClick={handleSubmitFeedback} variant="contained">
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

export default Help; 